import { PANEL_OPERATION_METADATA } from '@exylon/generated-contract-kernel/types';
import screenAccessManifest from 'virtual:screen-access';
import { isWorkspaceGranted, type PanelAccessModel, type PanelDestinationId } from '../foundation/access';
import type { PanelLiveScreenId } from '../foundation/runtime-port';
import { MENU_KEEPALIVE_OPERATION } from '../screens/menu/keepalive';
import { TPV_OFFLINE_OPERATION_NAMES, TPV_PROBE_OPERATION_TIMEOUTS_MS } from '../screens/tpv/offline/operations';
import { CANONICAL_UUID_PATTERN } from './format';
import { screenOperations, type PanelOperation } from './screen-access';
import type { PanelRuntimeSession } from './session-model';

export type PanelTenantKind = PanelRuntimeSession['tenant']['kind'];

/**
 * One row per Panel screen (row 5.4a, PS-01). The row is the screen's whole
 * admission: its routes, the tenant it belongs to, the workspace that admits
 * it, the closed operation vocabulary its transport client may dispatch and
 * how its presentation permissions are projected. The dispatcher in
 * `app/screen-runtime.tsx` and the Identity runtime's screen clients read
 * only this table; the Backend stays the authorization authority.
 *
 * `permissions`:
 * - `session` — the screen receives the session's access unchanged;
 * - `projected` — the registered vocabulary through `projectScreenPermissions`;
 * - `registered` — exactly the registered vocabulary's permissions (Constructor).
 */
export interface PanelScreenEntry {
  readonly paths: readonly string[];
  /** Route family matched in addition to `paths` (Super-admin franchise detail). */
  readonly pathPattern?: RegExp;
  readonly tenantKind: PanelTenantKind;
  /** The workspace that admits the route (it must be a visible destination),
   * the transport client and the screen. */
  readonly workspaceId?: PanelDestinationId;
  /** Home: a local view of a session whose navigation is enabled. */
  readonly navigationEnabled?: true;
  /** Configuración renders only with the manager authority the session
   * projects as the Constructor destination; its transport client admits any
   * restaurant session (the account settings live there). */
  readonly managerOnly?: true;
  readonly permissions: 'session' | 'projected' | 'registered';
  /** The screen's drawing has its own offline state: the shell neither freezes it nor writes its general sentence over it. */
  readonly drawsOwnOffline?: true;
  /** The screen's main action sits at the bottom of a narrow window: the navigation makes room for it. */
  readonly hasPrimaryAction?: true;
  /** The plain shell (no fixed menu button in a narrow window). */
  readonly plainShell?: true;
  /** The writes the screen wants finished even if the page closes while they travel. */
  readonly keepaliveOperations?: readonly PanelOperation[];
  readonly operations: readonly PanelOperation[];
}

/** A screen's operations, read from the compact manifest the build makes from `runtime/panel-screen-operations.ts`. */
const operations = (screen: PanelLiveScreenId): readonly PanelOperation[] => screenOperations(screenAccessManifest, screen);

export const PANEL_SCREENS = Object.freeze({
  // Service.
  tpv: { paths: ['/app/orders'], tenantKind: 'restaurant', workspaceId: 'POS', permissions: 'session', drawsOwnOffline: true, hasPrimaryAction: true, operations: operations('tpv') },
  cocina: { paths: ['/app/dishes'], tenantKind: 'restaurant', workspaceId: 'KITCHEN', permissions: 'session', hasPrimaryAction: true, operations: operations('cocina') },
  servicio: { paths: ['/app/waiterdelivers'], tenantKind: 'restaurant', workspaceId: 'SERVICE', permissions: 'session', hasPrimaryAction: true,
    operations: operations('servicio') },
  history: { paths: ['/app/order-history'], tenantKind: 'restaurant', workspaceId: 'CUSTOMER_HISTORY', permissions: 'projected',
    operations: operations('history') },
  // Content.
  menu: { paths: ['/app/menu'], tenantKind: 'restaurant', workspaceId: 'MENU', permissions: 'projected', keepaliveOperations: [MENU_KEEPALIVE_OPERATION], operations: operations('menu') },
  inventory: { paths: ['/app/inventory'], tenantKind: 'restaurant', workspaceId: 'INVENTORY', permissions: 'projected',
    operations: operations('inventory') },
  marketing: { paths: ['/app/promotions'], tenantKind: 'restaurant', workspaceId: 'MARKETING', permissions: 'projected',
    operations: operations('marketing') },
  tarjetas: { paths: ['/app/cards'], tenantKind: 'restaurant', workspaceId: 'LOYALTY', permissions: 'projected',
    operations: operations('tarjetas') },
  // Constructor is not a grantable workspace: its admission is the
  // restaurant-manager authority projected as the CONSTRUCTOR destination.
  constructor: { paths: ['/app/constructor'], tenantKind: 'restaurant', workspaceId: 'CONSTRUCTOR', permissions: 'registered',
    operations: operations('constructor') },
  // Operations.
  analytics: { paths: ['/app/analytics'], tenantKind: 'restaurant', workspaceId: 'ANALYTICS', permissions: 'projected',
    operations: operations('analytics') },
  hr: { paths: ['/app/employees'], tenantKind: 'restaurant', workspaceId: 'HR', permissions: 'projected', operations: operations('hr') },
  configuration: { paths: ['/app/config'], tenantKind: 'restaurant', managerOnly: true, permissions: 'projected',
    operations: operations('configuration') },
  // `/app/home` mounts El Parte (screens/parte), the Home local view. The Board is not a screen: it opens a destination
  // (docs/decisions/2026-11-04-board-selector-retired.md).
  'home-board': { paths: ['/app/home'], tenantKind: 'restaurant', navigationEnabled: true, plainShell: true, permissions: 'session',
    operations: operations('home-board') },
  // The session inbox on every restaurant page: no route of its own, exactly its four operations.
  inbox: { paths: [], tenantKind: 'restaurant', navigationEnabled: true, permissions: 'session', operations: operations('inbox') },
  franchise: { paths: ['/app/franchise', '/app/franchise/restaurants', '/app/franchise/settings'],
    tenantKind: 'franchise', permissions: 'session', operations: operations('franchise') },
  'super-admin': { paths: ['/app/super-admin', '/app/super-admin/settings', '/app/super-admin/franchises'],
    pathPattern: new RegExp(`^/app/super-admin/franchises/(?:new|${CANONICAL_UUID_PATTERN})$`, 'i'),
    tenantKind: 'platform', permissions: 'session',
    operations: operations('super-admin') },
} as const satisfies Record<PanelLiveScreenId, PanelScreenEntry>);

/** A screen a route can open; the session inbox has a client and no page. */
export type PanelRoutedScreenId = Exclude<PanelLiveScreenId, 'inbox'>;

const SCREEN_IDS = Object.keys(PANEL_SCREENS) as PanelLiveScreenId[];
const entry = (screen: PanelLiveScreenId): PanelScreenEntry => PANEL_SCREENS[screen];

/** One screen's row, as the shell reads it. */
export const panelScreenEntry = entry;

/** Every operation any screen client may dispatch (Identity's transport). */
export const PANEL_SCREEN_HTTP_OPERATIONS: readonly PanelOperation[] = Object.freeze([
  ...new Set(SCREEN_IDS.flatMap((screen) => entry(screen).operations)),
]);

/** The one route lookup: a path belongs to a screen only inside its tenant kind. */
export function panelScreenAtPath(path: string, tenantKind: PanelTenantKind): PanelRoutedScreenId | null {
  return SCREEN_IDS.find((screen): screen is PanelRoutedScreenId => {
    const row = entry(screen);
    return row.tenantKind === tenantKind && (row.paths.includes(path) || row.pathPattern?.test(path) === true);
  }) ?? null;
}

/** Presentation permissions for an already-admitted screen. Workspace and
 * domain admission remain at callers; transport/backend still authorize. */
function projectScreenPermissions(access: PanelAccessModel, registered: readonly PanelOperation[]): PanelAccessModel {
  const mutatingPermissions = new Set(registered
    .filter((operation) => PANEL_OPERATION_METADATA[operation].side_effect !== 'none')
    .map((operation) => PANEL_OPERATION_METADATA[operation].permission));
  const permissions = registered
    .filter((operation) => access.navigationRole === 'gerencia'
      || (PANEL_OPERATION_METADATA[operation].side_effect === 'none'
        && !mutatingPermissions.has(PANEL_OPERATION_METADATA[operation].permission)))
    .map((operation) => PANEL_OPERATION_METADATA[operation].permission);
  return { ...access, exactPermissions: [...new Set([...access.exactPermissions, ...permissions])] };
}

/**
 * The one screen access projection (C-E: the Panel never widens beyond what
 * this function returns). Management navigation (`navigation_scope:
 * 'management'` from /me/workspaces, 3.6a) receives the registered vocabulary;
 * other scopes receive only safe reads that no registered mutation shares. The
 * Panel derives no role itself: both inputs are server facts, and the result
 * is presentation only (transport and Backend still authorize).
 * Returns null when the screen's workspace (or manager authority) is absent.
 */
export function projectScreenAccess(access: PanelAccessModel, screen: PanelLiveScreenId): PanelAccessModel | null {
  const row = entry(screen);
  if (row.permissions === 'session') return access;
  if (row.workspaceId !== undefined && !isWorkspaceGranted(access, row.workspaceId)) return null;
  if (row.managerOnly && !isWorkspaceGranted(access, 'CONSTRUCTOR')) return null;
  if (row.permissions === 'registered') {
    return { ...access, exactPermissions: [...new Set(row.operations.map((operation) => PANEL_OPERATION_METADATA[operation].permission))] };
  }
  return projectScreenPermissions(access, row.operations);
}

/** Whether a session may hold this screen's transport client: tenant kind
 * and id, the admitting workspace and, for Home/Board, enabled navigation. */
export function screenAdmitsSession(session: PanelRuntimeSession, screen: PanelLiveScreenId): boolean {
  const row = entry(screen);
  const { tenant } = session;
  if (tenant.kind !== row.tenantKind) return false;
  if (row.workspaceId !== undefined && (tenant.kind !== 'restaurant' || !isWorkspaceGranted(tenant.access, row.workspaceId))) return false;
  return !row.navigationEnabled || tenant.kind === 'restaurant' && tenant.access.navigationEnabled;
}

/**
 * The paid Catalog AI helpers outlive the 15 s transport default. The Backend bounds
 * each provider call itself (text 60 s; the «Mejorar foto» image edit 180 s by default,
 * 270 s at most) and then meters and stages the answer, so the Panel waits past that
 * bound: giving up earlier abandons a call that is still running and being paid for,
 * and its retry could only answer `indeterminate`.
 */
const PANEL_AI_HELPER_TIMEOUTS_MS: Readonly<Record<string, number>> = Object.freeze({
  autocompleteCatalogDishIngredients: 90_000,
  proposeCatalogAiDishCopy: 90_000,
  proposeCatalogAiTranslation: 90_000,
  proposeCatalogAiPhotoEnhancement: 295_000,
});

/** Each screen's operations that outlive the transport default: Menú's AI helpers, the TPV's short probes. */
export const PANEL_OPERATION_TIMEOUTS_MS: Readonly<Record<string, number>> = Object.freeze({ ...PANEL_AI_HELPER_TIMEOUTS_MS, ...TPV_PROBE_OPERATION_TIMEOUTS_MS });
/** The writes a screen wants finished even if the page closes while they travel. */
export const PANEL_KEEPALIVE_OPERATIONS: readonly PanelOperation[] = Object.freeze(SCREEN_IDS.flatMap((screen) => entry(screen).keepaliveOperations ?? []));
/** The Service's public clock: what a device with no session asks to know whether anyone answers. */
export const PANEL_PUBLIC_CLOCK_OPERATION = TPV_OFFLINE_OPERATION_NAMES.readClock;
